
import { Recipe } from "@/types/recipe";
import { supabase } from "@/integrations/supabase/client";
import { authService } from "@/services/auth";
import { mapDbRecipeToRecipe } from "./recipeUtils";
import { withErrorHandling } from "../utils/errorHandling";
import { DbRecipe } from "@/types/database";
import { recipeStatsService, RecipeWithStats } from "./recipeStatsService";

/**
 * Service for querying recipes
 */
export const recipeQueryService = {
  /**
   * Get all recipes for the current user and their family with usage statistics
   */
  getRecipes: withErrorHandling(
    async (): Promise<RecipeWithStats[]> => {
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("Failed to get current user");
      }
      
      // With the new RLS policies, we can directly query recipes
      // The policy will automatically filter to show only family recipes
      const result = await supabase
        .from("recipes")
        .select("*")
        .order("created_at", { ascending: false });

      if (result.error) {
        throw result.error;
      }

      // Convert from database format to Recipe type
      const recipes = result.data.map((dbRecipe) => mapDbRecipeToRecipe(dbRecipe as DbRecipe));
      
      // Enhance recipes with usage statistics
      return await recipeStatsService.getRecipesWithStats(recipes);
    },
    "Error in getRecipes"
  ),

  /**
   * Get a recipe by ID
   */
  getRecipeById: withErrorHandling(
    async (id: string): Promise<Recipe | null> => {
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("Failed to get current user");
      }
      
      // Fetch the recipe - RLS will ensure only accessible recipes are returned
      const result = await supabase
        .from("recipes")
        .select("*")
        .eq("id", id)
        .single();

      if (result.error) {
        if (result.error.code === 'PGRST116') {
          // No rows returned - either doesn't exist or no access
          return null;
        }
        throw result.error;
      }

      if (!result.data) {
        return null;
      }

      // Convert from database format to Recipe type
      return mapDbRecipeToRecipe(result.data as DbRecipe);
    },
    "Error in getRecipeById"
  ),

  /**
   * Get recipes by specific IDs (optimized for composition detail pages)
   */
  getRecipesByIds: withErrorHandling(
    async (ids: string[]): Promise<Recipe[]> => {
      if (!ids || ids.length === 0) {
        return [];
      }

      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("Failed to get current user");
      }

      const result = await supabase
        .from("recipes")
        .select("*")
        .in("id", ids);

      if (result.error) {
        throw result.error;
      }

      if (!result.data) {
        return [];
      }

      // Convert from database format to Recipe type
      return result.data.map((dbRecipe) => mapDbRecipeToRecipe(dbRecipe as DbRecipe));
    },
    "Error in getRecipesByIds"
  ),

  /**
   * Get a recipe by suggestion ID
   */
  getRecipeBySuggestionId: withErrorHandling(
    async (suggestionId: string): Promise<Recipe | null> => {
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("Failed to get current user");
      }
      
      // Fetch the recipe by suggestion_id - RLS will ensure only accessible recipes are returned
      const result = await supabase
        .from("recipes")
        .select("*")
        .eq("suggestion_id", suggestionId)
        .maybeSingle();

      if (result.error) {
        throw result.error;
      }

      if (!result.data) {
        return null;
      }

      // Convert from database format to Recipe type
      return mapDbRecipeToRecipe(result.data as DbRecipe);
    },
    "Error in getRecipeBySuggestionId"
  )
};
